import React, { useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  LinearProgress,
  Button,
  Collapse,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useTheme,
  useMediaQuery
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import { useUsersManagement, FilterConfig } from '../../../hooks/useUsersManagement';
import UsersTable from './UsersTable';
import UserModals from './UserModals';

const UsersContainer: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const {
    users,
    totalCount,
    organizationInfo,
    availableRoles,
    loading,
    loadingOrgInfo,
    error,
    page,
    rowsPerPage,
    filterConfig,
    sortConfig,
    fetchUsers,
    fetchOrganizationInfo,
    handleFilterChange,
    clearFilters,
    handleSort,
    handleChangePage,
    handleChangeRowsPerPage
  } = useUsersManagement();

  const [showFilters, setShowFilters] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const handleEditUser = (user: any) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedUser(null);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleUserCreated = () => {
    fetchUsers();
    fetchOrganizationInfo();
  };

  const handleUserUpdated = () => {
    fetchUsers();
    fetchOrganizationInfo();
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant={isMobile ? "h6" : "h6"} gutterBottom>
        Manage Users
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View and manage users in your organization
      </Typography>

      {/* Seat Limit Information */}
      {loadingOrgInfo ? (
        <Box sx={{ width: '100%', mt: 1, mb: 4 }}>
          <LinearProgress />
        </Box>
      ) : organizationInfo && organizationInfo.plan !== 'INTERNAL' && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">
                User Seats: {organizationInfo.activeUsersCount} / {organizationInfo.seatLimit}
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={(organizationInfo.activeUsersCount / organizationInfo.seatLimit) * 100} 
              sx={{ height: 10, borderRadius: 5, mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              {organizationInfo.activeUsersCount >= organizationInfo.seatLimit 
                ? "You've reached your seat limit. To add more users, upgrade your plan."
                : `You can add ${organizationInfo.seatLimit - organizationInfo.activeUsersCount} more users to your organization.`
              }
            </Typography>
          </CardContent>
        </Card>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Responsive Controls */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        mb: 4,
        gap: { xs: 2, sm: 0 }
      }}>
        <Button 
          variant="outlined" 
          startIcon={<FilterListIcon />}
          onClick={() => setShowFilters(!showFilters)}
          color={Object.keys(filterConfig).length > 0 ? "primary" : "inherit"}
          fullWidth={isMobile}
        >
          {showFilters ? "Hide Filters" : "Show Filters"}
          {Object.keys(filterConfig).length > 0 && ` (${Object.keys(filterConfig).length})`}
        </Button>
        
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setIsCreateModalOpen(true)}
          disabled={!!(organizationInfo && organizationInfo.plan !== 'INTERNAL' && organizationInfo.activeUsersCount >= organizationInfo.seatLimit)}
          fullWidth={isMobile}
        >
          Create User
        </Button>
      </Box>
      
      {/* Filters Section */}
      <Collapse in={showFilters}>
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Filter Users
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">User Information</Typography>
                <Divider sx={{ mb: 2, mt: 0.5 }} />
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Email"
                  value={filterConfig.email || ''}
                  onChange={(e) => handleFilterChange('email', e.target.value)}
                  placeholder="Search by email"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="First Name"
                  value={filterConfig.firstName || ''}
                  onChange={(e) => handleFilterChange('firstName', e.target.value)}
                  placeholder="Search by first name"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Last Name"
                  value={filterConfig.lastName || ''}
                  onChange={(e) => handleFilterChange('lastName', e.target.value)}
                  placeholder="Search by last name"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={filterConfig.role || ''}
                    label="Role"
                    onChange={(e) => handleFilterChange('role', e.target.value)}
                  >
                    <MenuItem value="">Any</MenuItem>
                    {availableRoles.map(role => (
                      <MenuItem key={role} value={role}>{role}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterConfig.isActive === undefined ? '' : filterConfig.isActive ? 'active' : 'inactive'}
                    label="Status"
                    onChange={(e) => {
                      if (e.target.value === '') {
                        handleFilterChange('isActive', undefined);
                      } else {
                        handleFilterChange('isActive', e.target.value === 'active');
                      }
                    }}
                  >
                    <MenuItem value="">Any</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>Date Filters</Typography>
                <Divider sx={{ mb: 2, mt: 0.5 }} />
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Created After"
                  type="date"
                  size="small"
                  fullWidth
                  value={filterConfig.createdAfter || ''}
                  onChange={(e) => handleFilterChange('createdAfter', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Created Before"
                  type="date"
                  size="small"
                  fullWidth
                  value={filterConfig.createdBefore || ''}
                  onChange={(e) => handleFilterChange('createdBefore', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                  disabled={Object.keys(filterConfig).length === 0}
                  variant="outlined"
                  color="primary"
                  fullWidth={isMobile}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Collapse>

      {/* Users Table */}
      <UsersTable
        users={users}
        loading={loading}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        sortConfig={sortConfig}
        onEditUser={handleEditUser}
        onSort={handleSort}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />

      {/* Modals */}
      <UserModals
        isEditModalOpen={isEditModalOpen}
        isCreateModalOpen={isCreateModalOpen}
        selectedUser={selectedUser}
        onCloseEditModal={handleCloseEditModal}
        onCloseCreateModal={handleCloseCreateModal}
        onUserCreated={handleUserCreated}
        onUserUpdated={handleUserUpdated}
      />
    </Box>
  );
};

export default UsersContainer;